import React from 'react';

/**
 * GO-AROUND and DIVERTED badges for an arrival row (Predictions table), beside the callsign.
 *
 * The backend matches the day's GO_AROUND and DIVERSION track anomalies to the row:
 *   goAroundCount / lastGoAroundAt — go-arounds flown on this approach
 *   divertedAt / divertedTo / divertedLandedAt — divertedTo is null when the landing
 *   elsewhere was not seen (we only see landings where a receiver covers the airport)
 *
 * Each badge carries its own glyph and words, so the Mono theme loses nothing.
 */

/** Pure: the badges to show, each { kind, text, title }. `fmt` formats a server time as local HH:MM. */
export function approachFlags(row, fmt) {
  const out = [];
  if (!row) return out;
  const n = row.goAroundCount || 0;
  if (n > 0) {
    out.push({
      kind: 'goaround',
      text: `↺ Go-around${n > 1 ? ` ×${n}` : ''}`,
      title: `${n === 1 ? 'Went around' : `Went around ${n} times`} on this approach`
        + (row.lastGoAroundAt ? `${n > 1 ? ', last' : ''} at ${fmt(row.lastGoAroundAt)}` : '')
        + '. The landing estimate before it does not include the extra circuit.',
    });
  }
  if (row.divertedAt) {
    out.push({
      kind: 'diverted',
      text: `↪ Diverted${row.divertedTo ? ` → ${row.divertedTo}` : ''}`,
      title: row.divertedTo
        ? `Diverted to ${row.divertedTo} after a go-around or hold at ${fmt(row.divertedAt)}`
          + (row.divertedLandedAt ? `; landed there ${fmt(row.divertedLandedAt)}` : '')
        : `Probable diversion after a go-around or hold at ${fmt(row.divertedAt)}: it left without `
          + 'landing here. Destination unknown: we only see landings where a receiver covers the airport.',
    });
  }
  return out;
}

export default function ApproachFlags({ row, fmt }) {
  const flags = approachFlags(row, fmt);
  if (!flags.length) return null;
  return flags.map((f) => (
    <span key={f.kind} className={`pt-flag pt-${f.kind}`} title={f.title}>{f.text}</span>
  ));
}
